import { Component, EventEmitter, Inject, Input, Output, inject } from '@angular/core';
import {
  WMSEditorComponent,
} from '../../../components/wms-editor/wms-editor.component';
import { ApisService, WebserviceService } from '@wms/app/services';
import { CommonModule } from '@angular/common';
import { BaseComponent } from '@wms/components';
import { NavigationEnd, Router } from '@angular/router';
import { Subscription, filter } from 'rxjs';
import { EmptyScreenComponent } from '../empty-screen/empty-screen.component';
import { IrestImportUIConfig } from './interfaces/restImportUIConfig';
import { IBodyParams } from './interfaces/bodyParams';
import { ScriptLoaderService } from '@wm/core';
import {ApiWorkspaceService} from "@wms/app/api-workspace.service";

declare const window: any

@Component({
  standalone: true,
  selector: 'wms-rest-import-ui',
  templateUrl: './rest-import-ui.component.html',
  styleUrls: ['./rest-import-ui.component.less'],
  imports: [CommonModule, WMSEditorComponent, EmptyScreenComponent],
})
export class RestImportUiComponent extends BaseComponent {
  @Input() codeEditorValue: string;
  @Input() restImportDomID: string;
  @Input() restImportViewMode: boolean;
  private routerSubscription: Subscription;
  hideMonacoEditor: boolean = false;
  restImportConfig: IrestImportUIConfig = {} as IrestImportUIConfig;
  @Output() checkForUpdate = new EventEmitter<void>();
  restImportData = {
    requestBody: () => this.apiWorkspaceService.requestBody,
    responseBody: () => this.apiWorkspaceService.responseBody,
    importDisabled: (): boolean => this.apiWorkspaceService.importDisabled,
    restImportServiceName: (): string => this.apiWorkspaceService.restImportServiceName,
    settingsUploadData: () => this.apiWorkspaceService.settingsUploadData
  };
  restImportBundleLoading = false;
  private scriptLoaderService = inject(ScriptLoaderService);
  baseURL: string = 'assets';
  appBaseMetaInfo: any;

  constructor(@Inject("APP_BUILD_META_INFO") appBaseMetaInfo: any, private router: Router, private apiService: ApisService, private apiWorkspaceService: ApiWorkspaceService, private webService: WebserviceService) {
    super();
    this.appBaseMetaInfo = appBaseMetaInfo;
    this.baseURL = this.appBaseMetaInfo.CDN_BASE_URL ? this.appBaseMetaInfo.CDN_BASE_URL + this.baseURL : this.baseURL;
    this.routerSubscription = this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe((event: any) => {
        if (event.url.includes('project-id')) this.getServiceData();
      });
  }

  ngAfterViewInit() {
    this.restImportViewMode && this.getServiceData();
  }
  override ngOnDestroy() {
    this.routerSubscription.unsubscribe();
  }

  getServiceData() {
    const serviceName = this.router.url?.split('/')[3]?.split('?')[0];
    if (serviceName && this.router.url?.split('/')[2] === 'rest') {
      (
        this.webService.getRESTDetails({
          urlParams: {
            projectID: this.projectService.getProjectId(),
            serviceID: serviceName,
          },
        }) as Promise<any>
      )
        .then((response) => {
          const parameters = this.getParamsWithTypes(response);
          const headers =
            response.httpRequestDetails.sampleHttpResponseDetails.headers;
          this.restImportConfig.bodyParams =
            response.httpRequestDetails.requestBody;
          this.restImportConfig.httpMethod = response.httpRequestDetails.method;
          this.restImportConfig.contentType =
            response.httpRequestDetails.contentType;
          this.restImportConfig.httpAuth = {
            type: response.httpRequestDetails.authDetails?.type || '',
            providerId:
              response.httpRequestDetails?.authDetails?.providerId || '',
          };

          this.restImportConfig.withCredentials = response.proxySettings.withCredentials
          for (const key in headers) {
            if (Array.isArray(headers[key])) {
              headers[key] = headers[key].join('');
            }
          }
          this.apiWorkspaceService.responseBody = response.httpRequestDetails.sampleHttpResponseDetails.responseBody;
          this.restImportConfig.setResponseHeaders = headers;
          this.restImportConfig.queryParams = this.constructHeaderandQueryParams(
            response.httpRequestDetails.queryParams,
            parameters,
            'query'
          );
          this.restImportConfig.headerParams = this.constructHeaderandQueryParams(
            response.httpRequestDetails.headers,
            parameters,
            'header'
          );
          this.restImportConfig.multipartParams = this.constructMultiParams(response.httpRequestDetails.multiParamInfoList)
          this.apiWorkspaceService.restImportServiceName = serviceName;
          const hasQueryParams =
            Object.keys(response.httpRequestDetails.queryParams).length > 0;
          this.restImportConfig.url =
            response.httpRequestDetails.endpointAddress +
            `${hasQueryParams
              ? '?' +
              this.constructURL(response.httpRequestDetails.queryParams)
              : ''
            }`;
          this.restImportConfig.useProxy = response.proxySettings.web === 'PROXY' ? true : false;
          this.restImportConfig.urlBasePath = response.httpRequestDetails.urlBasePath;
          this.restImportConfig.settingsDetailsResponse = response;
          this.apiWorkspaceService.settingsUploadData = response;
          this.initiateRestImportUI()
        })
        .catch((error) => {
          this.router.navigate(['apis'])
          console.log(error)
        });
    }
  }

  private checkforRequirJS(): Promise<boolean> {
    return new Promise((resolve, reject) => {
      if (this.scriptLoaderService.isRequireScriptLoaded(this.baseURL)) {
        resolve(true)
      } else {
        const wmsEditorLoaderScript: HTMLScriptElement = document.head.appendChild(this.scriptLoaderService.createLoaderScriptElement(this.baseURL))
        wmsEditorLoaderScript.onload = (): void => {
          this.configureRequire(() => {
            resolve(true)
          })
          wmsEditorLoaderScript.onerror = (err): void => {
            reject(false)
            console.error('Failed to load require.js', err)
          }
        }
      }
    })
  }
  async getEnvProps() {
    await (this.projectService.appEnvironmentProperties() as Promise<any>).then((data: string[]) => {
      if (data) {
        this.restImportConfig.appEnvVariables = []
        data.map((key: string) => {
          if (key.startsWith('app.environment')) {
            this.restImportConfig.appEnvVariables.push({ name: key.replace('app.environment.', ''), type: 'APP_ENVIRONMENT', value: '' })
          }
        })
      }
    }).catch((error) => {
      console.error(error)
    })
  }

  private configureRequire(callback: Function) {
    window.require([`${this.baseURL}/require.config.js`], (configFunction: any) => {
      configFunction(this.baseURL)
      callback()
    })
  }

  async initiateRestImportUI() {
    if (await this.checkforRequirJS()) {
      await this.getEnvProps()
      const configData: IrestImportUIConfig = {
        url:
          this.restImportConfig.url ||
          'https://maps.googleapis.com/maps/api/directions/xml?origin=Toronto&destination=Montreal&sensor=false',
        viewMode: this.restImportViewMode,
        projectId: this.projectService.getProjectId(),
        httpAuth: this.restImportConfig.httpAuth,
        httpMethod: this.restImportConfig.httpMethod,
        contentType: this.restImportConfig.contentType,
        bodyParams: this.restImportConfig.bodyParams,
        setServiceName: this.restImportViewMode ? this.apiWorkspaceService.restImportServiceName : '',
        useProxy: this.restImportViewMode ? this.restImportConfig.useProxy : true,
        withCredentials: this.restImportConfig.withCredentials,
        headerParams: this.restImportConfig.headerParams,
        queryParams: this.restImportConfig.queryParams,
        multipartParams: this.restImportConfig.multipartParams,
        setResponseHeaders: this.restImportViewMode ? this.restImportConfig.setResponseHeaders : '',
        setResponse: this.restImportViewMode ? this.apiWorkspaceService.responseBody : '',
        responseType: this.restImportViewMode ? this.getResponseContentType(this.apiWorkspaceService.responseBody) : '',
        appEnvVariables: this.restImportConfig.appEnvVariables,
        proxy_conf: {
          base_path: window.location.protocol + '//' + window.location.host + '/',
          proxy_path: `studio/services/projects/${this.projectService.getProjectId()}/restservices/invoke?optimizeResponse=true`,
          list_provider: `studio/services/oauth2/providers/default`,
          getprovider: `studio/services/projects/${this.projectService.getProjectId()}/oauth2/providers`,
          addprovider: `studio/services/projects/${this.projectService.getProjectId()}/oauth2/providers`,
          authorizationUrl: `studio/services/projects/${this.projectService.getProjectId()}/oauth2/:providerID/authorizationUrl`, // Needs params ID here
          settingsUpload: `studio/services/projects/${this.projectService.getProjectId()}/restservice/generateSwagger`,
          updateSwagger: `studio/services/projects/${this.projectService.getProjectId()}/restservice/updateSwagger`
        },
        state_val:
          'eyJtb2RlIjoiZGVzaWduVGltZSIsInByb2plY3RJZCI6IldNUFJKMmM5MTgwODg4OWE5NjQwMDAxOGExYzE0YjBhNzI4YTQifQ==',
        error: {
          errorFunction: (msg: string, response?: any) => {
            alert(msg);
            console.log(response);
          },
          errorMethod: 'default', // Error method used when error function is called with error message
          // errorMessageTimeout: 5000, // timeout in milliseconds
        },
        monacoEditorURL: undefined as any,
        handleResponse: (
          request: any,
          response: any,
          settingsUploadData: any
        ) => {
          if (response) {
            this.apiWorkspaceService.requestBody = request;
            this.apiWorkspaceService.responseBody = response;
            if (settingsUploadData !== undefined) {
              this.apiWorkspaceService.settingsUploadData = settingsUploadData;
              this.apiWorkspaceService.restImportServiceName = settingsUploadData.serviceId;
              this.updateDisabled();
            }
          }
        },
        getServiceName: (value: string) => {
          if (value.trim() !== '') {
            this.apiWorkspaceService.importDisabled = false;
          } else {
            this.apiWorkspaceService.importDisabled = true;
          }
          this.apiWorkspaceService.restImportServiceName = value;
        },
        hideMonacoEditor: (value: boolean) => {
          this.hideMonacoEditor = value;
        },
        getUseProxy: (value) => {
          if (this.apiWorkspaceService.settingsUploadData) {
            this.apiWorkspaceService.settingsUploadData['proxySettings'].mobile = value ? 'PROXY' : 'DIRECT';
            this.apiWorkspaceService.settingsUploadData['proxySettings'].web = value ? 'PROXY' : 'DIRECT';
          }
        },
        handleUpdateSwaggerResponse : (updateSwaggerResponse) => {
          this.apiWorkspaceService.settingsUploadData = updateSwaggerResponse;
        },
        urlBasePath: this.restImportConfig?.urlBasePath ? this.restImportConfig?.urlBasePath : '',
        settingsDetailsResponse: this.restImportConfig?.settingsDetailsResponse ? this.restImportConfig?.settingsDetailsResponse : {}
      };
      if ((this.apiService.importDialogOpened && !this.restImportViewMode) || (!this.apiService.importDialogOpened && this.restImportViewMode)) {
        this.restImportBundleLoading = true;
        window.require(['restClientUI/rest-import-bundle'], (RestImport: any) => {
          this.restImportBundleLoading = false;
          this.restImportDomID && RestImport({
            dom_id: `#${this.restImportDomID}`,
            language: 'en',
            config: configData,
          });
        }, (error: any) => {
          console.error('Loading rest-import-bundle failed', error);
        })
      }
    }
  }

  updateDisabled() {
    this.checkForUpdate.emit();
  }
  getType(name: string, paramaters: any[], from: string): string {
    let type = 'string';
    paramaters.map((params) => {
      if (params.in === from) {
        if (name === params.name) {
          type = params.format.replace("__APP_ENV__", "");
        }
      }
    });
    return type;
  }
  constructMultiParams(data: any[]): any[] {
    const params: IBodyParams[] = [];
    function getType(name: string): string {
      const type = name ? name.toLowerCase() : 'file';
      if (type === 'string') {
        return 'text';
      }
      return type
    }
    data?.forEach((param: { contentType: string, name: string, type: string, list: boolean, testValue: string }) => {
      if (param.name !== '') {
        params.push({ name: param.name, type: param.type.toLowerCase(), value: param.testValue, filename: '', contentType: getType(param.contentType || param.type) });
      }
    })
    return params;
  }
  constructHeaderandQueryParams(
    data: any,
    paramaters: any[],
    from: string
  ): any[] {
    const params = [];
    for (const key in data) {
      if (data.hasOwnProperty(key)) {
        const name = key;
        const value = data[key][0];
        const type = this.getType(name, paramaters, from);
        params.push({ name, type, value });
      }
    }
    return params;
  }
  constructURL(data: any) {
    const params = [];
    for (const key in data) {
      if (data.hasOwnProperty(key)) {
        params.push(key + '=' + data[key]);
      }
    }
    return params.join('&');
  }
  getParamsWithTypes(response: any) {
    const swaggerPaths = response.swagger.paths;
    const [firstKey] = Object.keys(swaggerPaths);
    const firstObject = swaggerPaths[firstKey];
    const [secondKey] = Object.keys(firstObject);
    const secondObject = firstObject[secondKey];
    const paramaters = secondObject.parameters || [];
    return paramaters;
  }

  // Check if a string is valid JSON
  isJSON(str: string): boolean {
    try {
      JSON.parse(str);
      return true;
    } catch (e) {
      return false;
    }
  }

  // Check if a string is valid XML
  isXML(str: string): boolean {
    const parser = new DOMParser();
    const parsedDoc = parser.parseFromString(str, 'application/xml');
    return parsedDoc.getElementsByTagName('parsererror').length === 0;
  }

  getResponseContentType(response: string) {
    if (this.isJSON(response))
      return 'json'
    else if (this.isXML(response))
      return 'xml'
    else
      return 'plaintext'
  }
}
